import React, { useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown from 'components/base/RevealDropdown';
import HVASpinner from 'components/base/HVASpinner';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  deleteHVAScorecard,
  updateHVAScorecard,
  HVAScorecard,
  HVAScorecardQuestion,
  fetchHVAScorecardQuestions
} from 'services/hvascorecard.service';
import ScorecardQuestionsList from '../../pages/hvaqualityassurance/ScorecardQuestionsList';

type ScorecardActionCellProps = {
  id: number;
  name: string;
  systemdefault: boolean;
  production: boolean;
  active: boolean;
  onUpdate: () => void;
};

const ScorecardActionCell = ({
  id,
  name,
  systemdefault,
  production,
  active,
  onUpdate
}: ScorecardActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState<HVAScorecard>({
    id,
    name,
    systemdefault,
    production,
    active,
    questions: []
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (production && name !== 'active') {
      return; // Don't allow changes to other fields if in production
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleQuestionsChange = (questions: HVAScorecardQuestion[]) => {
    if (!production) {
      setFormData({
        ...formData,
        questions
      });
    }
  };

  const resetForm = () => {
    setFormData({
      id,
      name,
      systemdefault,
      production,
      active,
      questions: []
    });
  };

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    resetForm();
  };

  const handleShowEditModal = async () => {
    try {
      const questions = await fetchHVAScorecardQuestions(id);
      setFormData({
        ...formData,
        questions: questions || [] // Ensure questions is always an array
      });
      setShowEditModal(true);
    } catch (error) {
      console.error(`Error fetching questions for scorecard ${id}:`, error);
      // Even if there's an error, we still want to show the modal with empty questions
      setFormData({
        ...formData,
        questions: []
      });
      setShowEditModal(true);
    }
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteHVAScorecard(id);
      console.log(`Scorecard with ID: ${id} deleted successfully`);
      onUpdate(); // Actualizar la tabla después de eliminar
    } catch (error) {
      console.error(`Error deleting Scorecard with ID: ${id}`, error);
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleUpdateScorecard = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const dataToUpdate = production
        ? { id: formData.id, active: formData.active }
        : formData;
      await updateHVAScorecard(id, dataToUpdate);
      // console.log(`Scorecard with ID: ${id} updated successfully`);
      onUpdate(); // Actualizar la tabla después de editar
      handleCloseEditModal();
    } catch (error) {
      console.error(`Error updating Scorecard with ID: ${id}`, error);
    }
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        {!production && (
          <Dropdown.Item
            onClick={handleShowDeleteModal}
            className="text-danger"
          >
            <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
            Remove
          </Dropdown.Item>
        )}
      </RevealDropdown>

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this Scorecard?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        centered
        size="xl"
      >
        <Form onSubmit={handleUpdateScorecard}>
          <Modal.Header closeButton>
            <Modal.Title>
              {production ? 'Update Scorecard Status' : 'Update Scorecard'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={production}
              />
              <div className="form-check form-switch mb-0 pt-2">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="systemdefault"
                    value="systemdefault"
                    checked={formData.systemdefault}
                    onChange={handleInputChange}
                    className="form-check-input"
                    disabled={production}
                  />
                  To be used as the Default scorecard for a campaign
                </label>
              </div>
              <div className="form-check form-switch mb-0">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="production"
                    value="production"
                    checked={formData.production}
                    onChange={handleInputChange}
                    className="form-check-input"
                    disabled={production}
                  />
                  This scorecard is in Production mode
                </label>
              </div>
              <div className="form-check form-switch pb-3">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    value="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  This scorecard is Active
                </label>
              </div>
            </Form.Group>
            {!production && (
              <ScorecardQuestionsList
                questions={formData.questions}
                onQuestionsChange={handleQuestionsChange}
              />
            )}
            {production && (
              <p className="text-muted">
                This scorecard is in production. Only its active status can be
                modified.
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Scorecard
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

// eslint-disable-next-line
const ExpandedRowContent: React.FC<{scorecardId: number}> = ({ scorecardId }) => {
  // eslint-disable-next-line
  const [questions, setQuestions] = useState<HVAScorecardQuestion[] | null>(null);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const fetchedQuestions = await fetchHVAScorecardQuestions(scorecardId);
        setQuestions(fetchedQuestions);
      } catch (error) {
        console.error(`Error fetching scorecard questions: `, error);
        setQuestions([]);
      }
    };

    loadQuestions();
  }, [scorecardId]);

  if (questions === null) {
    return (
      <div className="p-3 bg-body text-center">
        <HVASpinner />
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <div className="p-3 pb-8 bg-body">
        <p>No questions available for this scorecard.</p>
      </div>
    );
  }

  return (
    <div className="p-3 bg-body">
      <h6 className="mb-2">Questions:</h6>
      <ul className="list-unstyled">
        {questions.map((question, index) => (
          <li key={index} className="mb-2">
            <span
              className="d-inline-block bullet-item me-2 bg-primary"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '30%'
              }}
            />
            <strong>{question.questiontitle}</strong>
            <p className="mb-0 text-muted px-3">{question.questiontext}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const hvascorecardsTableColumns = (
  onUpdate: () => void
): ColumnDef<HVAScorecard>[] => [
  {
    accessorKey: 'name',
    header: 'Scorecard Title',
    cell: ({ row }) => {
      const { name } = row.original;
      return <div className="fw-bold">{name}</div>;
    },
    meta: {
      headerProps: { style: { width: '30%' } }
    }
  },
  {
    accessorKey: 'action',
    header: '',
    enableSorting: false,
    cell: ({ row }) => (
      <ScorecardActionCell {...row.original} onUpdate={onUpdate} />
    ),
    meta: {
      headerProps: { style: { width: '5%' } }
    }
  },
  {
    accessorKey: 'systemdefault',
    header: 'Campaign Default',
    cell: ({ row }) => {
      const { systemdefault } = row.original;
      return <div>{systemdefault ? 'Yes' : 'No'}</div>;
    },
    meta: {
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'production',
    header: 'Production mode',
    cell: ({ row }) => {
      const { production } = row.original;
      return <div>{production ? 'Yes' : 'No'}</div>;
    },
    meta: {
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'active',
    header: 'Active',
    cell: ({ row }) => {
      const { active } = row.original;
      return <div>{active ? 'Yes' : 'No'}</div>;
    },
    meta: {
      headerProps: { style: { width: '45%' } }
    }
  }
];

export const HVAScorecardsTable: React.FC = () => {
  return (
    <div>
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
        renderRowSubComponent={({ row }) => {
          const scorecardId = row.original.id;
          return (
            <div className="mb-n3 mt-n3 pb-0 pt-0 bg-body">
              <ExpandedRowContent scorecardId={scorecardId} />
            </div>
          );
        }}
      />
      <AdvanceTableFooter pagination />
    </div>
  );
};
